// src/lib/password.client.ts — câmp de parolă cu buton „arată parola" (constatare UX 26.08),
// PARTAJAT de login/înregistrare, profil (schimbarea parolei) și admin (parola inițială).
// Construit ca INPUT-GROUP: chenarul + focus-ring-ul sunt ale WRAPPER-ului, deci butonul stă
// garantat în interiorul casetei indiferent de layoutul paginii (poziționarea absolută peste
// input-uri cu lățimi diferite se rupea). Iconițele sunt SVG pe `currentColor` (nu emoji —
// randare identică pe orice platformă, colorabilă pe temă).
import van from "vanjs-core"
import type { State } from "vanjs-core"

const t = van.tags
const s = van.tags("http://www.w3.org/2000/svg")

const ICON_ATTRS = {
	width: "18",
	height: "18",
	viewBox: "0 0 24 24",
	fill: "none",
	stroke: "currentColor",
	"stroke-width": "1.7",
	"stroke-linecap": "round",
	"stroke-linejoin": "round",
}

/** Ochi deschis / ochi tăiat (contururile Heroicons, MIT). */
function eye_icon(open: boolean): Element {
	return open
		? s.svg(
				ICON_ATTRS,
				s.path({
					d: "M2.06 12.35a1 1 0 0 1 0-.7C3.42 7.51 7.36 4.5 12 4.5s8.58 3.01 9.94 7.15a1 1 0 0 1 0 .7C20.58 16.49 16.64 19.5 12 19.5S3.42 16.49 2.06 12.35Z",
				}),
				s.circle({ cx: "12", cy: "12", r: "3" }),
			)
		: s.svg(
				ICON_ATTRS,
				s.path({
					d: "M3.98 8.22A10.5 10.5 0 0 0 1.93 12c1.3 4.34 5.31 7.5 10.07 7.5.99 0 1.95-.14 2.86-.4M6.23 6.23A10.45 10.45 0 0 1 12 4.5c4.76 0 8.77 3.16 10.07 7.5a10.52 10.52 0 0 1-4.3 5.77M6.23 6.23 3 3m3.23 3.23 3.65 3.65m7.89 7.89L21 21m-3.23-3.23-3.65-3.65m0 0a3 3 0 1 0-4.24-4.24m4.24 4.24L9.88 9.88",
				}),
			)
}

/**
 * Câmp de parolă cu comutatorul „arată parola". Întoarce și `input` (element) — profilul
 * citește/golește `.value` direct; `value` (State) e pentru legarea reactivă din admin
 * (golirea după submit). `compact` = varianta de rând din admin (mai mică, lățime fixă).
 */
export function password_field(
	opts: {
		placeholder?: string
		autocomplete?: string
		required?: boolean
		value?: State<string>
		oninput?: (v: string) => void
		compact?: boolean
	} = {},
): { el: HTMLElement; input: HTMLInputElement } {
	const show = van.state(false)
	const input = t.input({
		class: "flex-1 min-w-0 bg-transparent outline-none text-sm placeholder:text-slate-400",
		type: () => (show.val ? "text" : "password"),
		...(opts.placeholder ? { placeholder: opts.placeholder } : {}),
		...(opts.autocomplete ? { autocomplete: opts.autocomplete } : {}),
		...(opts.required ? { required: true } : {}),
		...(opts.value ? { value: opts.value } : {}),
		oninput: (e: Event) => opts.oninput?.((e.target as HTMLInputElement).value),
	}) as HTMLInputElement
	const el = t.div(
		{
			class:
				"flex items-center rounded-lg border border-slate-300 dark:border-slate-600 " +
				(opts.compact ? "w-52 px-2.5 py-1.5 " : "w-full px-3 py-2 ") +
				"focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500/20 transition",
		},
		input,
		t.button(
			{
				type: "button", // fără type, ar trimite formularul în care stă
				tabindex: -1, // Tab-ul merge direct la submit — ochiul e pentru mouse/tap
				"aria-label": "Arată sau ascunde parola",
				title: () => (show.val ? "Ascunde parola" : "Arată parola"),
				class:
					"shrink-0 ml-2 p-1 -m-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer",
				onclick: () => (show.val = !show.val),
			},
			// AMBELE iconițe montate STATIC, comutate prin class — regula Van din CLAUDE.md:
			// un nod returnat dintr-o funcție reactivă se poate DUBLA la re-randare (văzut live
			// 26.08: ambii ochi apăreau la prima tastă).
			t.span({ class: () => (show.val ? "" : "hidden") }, eye_icon(true)),
			t.span({ class: () => (show.val ? "hidden" : "") }, eye_icon(false)),
		),
	)
	return { el, input }
}
